import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { superAdminApi } from '@/api/superAdmin';
import { defaultFinancialReportHospitalId } from '@/constants/pnlDefaultScope';

export type ReportHospitalScopeDefault = 'consolidated' | 'financial';

/**
 * Resolve hospitalId for Intelligence API calls.
 * Chain/investor consolidated scope must send explicit `hospitalId=all` so the axios
 * interceptor does not replace it with activeHospitalId.
 */
export function intelligenceReportHospitalId(opts: {
  showHospitalFilter: boolean;
  selectedHospitalId: string;
  isChainScoped: boolean;
}): string | undefined {
  const { showHospitalFilter, selectedHospitalId, isChainScoped } = opts;
  if (!showHospitalFilter) return undefined;
  if (isChainScoped) {
    return !selectedHospitalId || selectedHospitalId === 'all' ? 'all' : selectedHospitalId;
  }
  if (!selectedHospitalId || selectedHospitalId === 'all') return undefined;
  return selectedHospitalId;
}

/**
 * Hospital picker + explicit `reportHospitalId` for chain/super report API calls.
 * Chain admins must pass hospitalId (including "all") so the axios interceptor does not
 * silently replace it with activeHospitalId.
 */
export function useChainReportHospitalScope(opts?: {
  defaultScope?: ReportHospitalScopeDefault;
}) {
  const { user, activeHospitalId } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  /** Match AuthContext: CHAIN scope or multi-hospital tenant owner (groupId + 2+ sites). */
  const isChainAdmin =
    user?.role === 'HOSPITAL_ADMIN' &&
    (user?.adminScope === 'CHAIN' ||
      (!!user?.groupId && (user?.hospitals?.length ?? 0) > 1));
  const isInvestor = user?.role === 'INVESTOR';
  const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
  const isChainScoped = isChainAdmin || investorHasChainScope;
  const showHospitalFilter = isSuperAdmin || isChainScoped;
  const chainHospitals = user?.hospitals ?? [];

  const defaultHospitalId = useMemo(() => {
    if (opts?.defaultScope === 'consolidated') {
      if (isSuperAdmin) return '';
      if (isChainScoped) return 'all';
      return '';
    }
    return defaultFinancialReportHospitalId({
      isSuperAdmin,
      isChainScoped,
      activeHospitalId,
      fallbackHospitalId: chainHospitals[0]?.id ?? null,
    });
  }, [
    opts?.defaultScope,
    isSuperAdmin,
    isChainScoped,
    activeHospitalId,
    chainHospitals,
  ]);

  const [selectedHospitalId, setSelectedHospitalId] = useState(defaultHospitalId);

  useEffect(() => {
    if (!showHospitalFilter) return;
    if (!selectedHospitalId && defaultHospitalId) {
      setSelectedHospitalId(defaultHospitalId);
    }
  }, [showHospitalFilter, defaultHospitalId, selectedHospitalId]);

  const { data: superHospitals } = useQuery({
    queryKey: ['super-admin-hospitals'],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  const hospitalOptions = useMemo(() => {
    if (isSuperAdmin) {
      return (superHospitals ?? []) as Array<{ id: string; name: string }>;
    }
    return chainHospitals;
  }, [isSuperAdmin, superHospitals, chainHospitals]);

  const reportHospitalId: string | undefined = isSuperAdmin
    ? selectedHospitalId || undefined
    : isChainScoped
      ? selectedHospitalId || defaultHospitalId || 'all'
      : undefined;

  return {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isChainScoped,
    isSuperAdmin,
    isChainAdmin,
  };
}
